/**
 * The show sheet's header, stats and synopsis.
 *
 * The previous version crowded the year, episode count, airing state and a
 * yellow IMDb badge into one line, each with its own size, weight and colour —
 * five typographic treatments competing inside 40 pixels of height. This is
 * built the other way round: a fixed, small set of roles, each used everywhere
 * it applies and nowhere else.
 *
 *   title    20/600    the show's name
 *   meta     13/500    year, network, episode count — one line, one treatment
 *   genres   12/500    muted, secondary to meta
 *   label    10/600    uppercase, above a stat
 *   value    15/600    the stat itself
 *   body     13.5/400  the synopsis
 *
 * Facts that used to be chips are plain text now. A badge says "this is
 * different from its surroundings", and six of them side by side say nothing
 * at all, so the rating and the airing state became stats with labels instead
 * — which is also what gives "7.8" a word explaining it.
 */
import { useState } from "react";

import type { LibraryShow } from "@/api/schemas";
import { PosterViewer } from "@/components/PosterViewer";
import { remaining } from "@/lib/episode";
import { bucketOf } from "@/lib/grouping";
import { posterUrl } from "@/lib/poster";
import type { SeasonView } from "@/lib/seasons";
import type { ShowDetail } from "@/store/details";

/** At most three. A show tagged with six genres is not telling you anything. */
const MAX_GENRES = 3;

function join(parts: readonly (string | number | null)[]): string {
  return parts.filter((part) => part !== null && part !== "").join(" · ");
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
    </div>
  );
}

/**
 * Rating, airing state and runtime, sharing one treatment.
 *
 * Cells only appear when they have something to say, so a show with no IMDb
 * entry gets two cells rather than a gap where a number should be.
 */
export function Stats({ show, detail }: { show: LibraryShow; detail: ShowDetail | null }) {
  const airing = detail === null ? show.notAiredEpisodes > 0 : detail.airing;
  const runtime = show.show.runtime;
  return (
    <div className="stats">
      {detail !== null && detail.imdbRating !== null && (
        <Stat label="IMDb" value={detail.imdbRating.toFixed(1)} />
      )}
      <Stat label="Status" value={airing ? "Airing" : "Ended"} />
      {runtime !== null && runtime > 0 && <Stat label="Episode" value={`${String(runtime)} min`} />}
    </div>
  );
}

export function Head({
  show,
  seasons,
  detail,
}: {
  show: LibraryShow;
  seasons: readonly SeasonView[];
  detail: ShowDetail | null;
}) {
  const [viewing, setViewing] = useState(false);
  const poster = posterUrl(show.show.poster, "row");
  const full = posterUrl(show.show.poster, "full");
  const total = seasons.reduce((sum, season) => sum + season.episodes.length, 0);
  const watched = seasons.reduce((sum, season) => sum + season.watchedCount, 0);
  const fraction = total === 0 ? 0 : Math.min(1, watched / total);
  const meta = join([
    show.show.year,
    detail?.network ?? null,
    total === 0 ? null : `${String(total)} episodes`,
  ]);
  const genres = join(detail?.genres.slice(0, MAX_GENRES) ?? []);

  return (
    <div className="sheet-head">
      <div className="sheet-ident">
        {poster === null ? (
          <span className="poster" />
        ) : (
          <button
            type="button"
            className="poster-button"
            aria-label={`View the ${show.show.title} poster`}
            onClick={() => {
              setViewing(true);
            }}
          >
            <img className="poster" src={poster} alt="" decoding="async" />
          </button>
        )}
        <div className="sheet-info">
          <h2 className="sheet-title">{show.show.title}</h2>
          <p className="sheet-meta">{meta}</p>
          {genres !== "" && <p className="sheet-genres">{genres}</p>}
        </div>
      </div>
      <div className="sheet-progress">
        <div className={`bar ${bucketOf(show, detail?.airing)}`}>
          <span style={{ transform: `scaleX(${String(fraction)})` }} />
        </div>
        <p className="sheet-count">
          {total === 0
            ? `${String(watched)} watched`
            : `${String(watched)} of ${String(total)} watched · ${String(remaining(show))} left`}
        </p>
      </div>
      {viewing && full !== null && (
        <PosterViewer
          src={full}
          title={show.show.title}
          onClose={() => {
            setViewing(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * The synopsis, three lines until asked for the rest.
 *
 * Clamped with line-clamp rather than by cutting the string, so the full text
 * is always in the DOM: expanding is a class change, and nothing has to guess
 * where a sentence ends.
 */
export function Overview({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="sheet-overview">
      <p className={expanded ? "overview" : "overview clamped"}>{text}</p>
      <button
        type="button"
        className="more"
        aria-expanded={expanded}
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </div>
  );
}
